import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, notifyError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { useAuth } from "../contexts/AuthContext";
import CustomerFormDialog from "../components/CustomerFormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Phone, Car, ArrowUpRight, Loader2 } from "lucide-react";

export default function Customers() {
  const { canManage } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setList(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const startCreate = () => {
    setEditing(null);
    setOpen(true);
  };

  const startEdit = (c) => {
    setEditing(c);
    setOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/customers/${deleteId}`);
      toast.success("Müşteri silindi");
      setDeleteId(null);
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-amber-600">Yönetim</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
            Müşteriler
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Toplam {list.length} müşteri kayıtlı.
          </p>
        </div>
        <Button onClick={startCreate} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="add-customer-btn">
          <Plus className="h-4 w-4 mr-2" /> Yeni Müşteri
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Ad, telefon veya plaka ara..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-10 h-11 rounded-sm bg-card"
          data-testid="customer-search"
        />
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center rounded-sm">
          <div className="font-display font-bold text-lg">Henüz müşteri yok</div>
          <p className="text-sm text-muted-foreground mt-2">İlk müşterinizi ekleyerek başlayın.</p>
          <Button onClick={startCreate} className="mt-4 rounded-sm">
            <Plus className="h-4 w-4 mr-2" /> Müşteri Ekle
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((c) => (
            <Card key={c.id} className="surface-card p-5 rounded-sm group" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-lg tracking-tight truncate">
                    {c.full_name}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                    <Phone className="h-3.5 w-3.5" />
                    <span className="font-mono">{c.phone}</span>
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => startEdit(c)}
                    className="p-2 hover:bg-muted rounded-sm"
                    data-testid={`edit-customer-${c.id}`}
                    aria-label="Düzenle"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  {canManage && (
                    <button
                      onClick={() => setDeleteId(c.id)}
                      className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-sm"
                      data-testid={`delete-customer-${c.id}`}
                      aria-label="Sil"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {(c.vehicles?.length > 0 || c.plate) && (
                <div className="mt-4 pt-4 border-t border-border">
                  {(c.vehicles && c.vehicles.length > 0 ? c.vehicles : [{ plate: c.plate, brand: c.vehicle_brand, model: c.vehicle_model }]).slice(0, 2).map((v, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm mb-1">
                      <Car className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        {v.plate && <span className="font-mono font-bold tracking-wider mr-2">{v.plate}</span>}
                        {(v.brand || v.model) && (
                          <span className="text-xs text-muted-foreground">
                            {[v.brand, v.model].filter(Boolean).join(" ")}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {c.vehicles && c.vehicles.length > 2 && (
                    <div className="text-xs text-muted-foreground mt-1 ml-7">+{c.vehicles.length - 2} araç daha</div>
                  )}
                </div>
              )}

              <Link
                to={`/musteriler/${c.id}`}
                className="mt-4 flex items-center justify-between text-sm font-semibold text-foreground hover:text-amber-600 transition-colors"
                data-testid={`view-customer-${c.id}`}
              >
                <span>Detay & Emanetler</span>
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <CustomerFormDialog
        open={open}
        onOpenChange={setOpen}
        customer={editing}
        onSaved={() => load()}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Müşteri silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Müşterinin geçmiş emanet ve ödemeleri de silinecektir.
              Eğer aktif (depoda) emaneti varsa silme engellenir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-customer">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
